import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ImageSearchResultType, ImageSearchResponseType } from '../../types/type';
import { useCompressImageMutation } from '../../../hooks/mutations/useCompressedImageMutation';
import { useSearchImageMutation } from '../../hooks/useSearchImageMutation';

import ImageSearchLoading from './ImageSearchLoading';
import ImageSearchFailure from './ImageSearchFailure';
import ImageSearchSuccessContainer from './ImageSearchSuccessContainer';

interface ImageSearchResultProps extends ImageSearchResultType {
  onBackClick: () => void;
  onAnotherSearchClick: () => void;
  mode?: string;
}

const MIN_SIMILARITY_THRESHOLD = 0.4;

export default function ImageSearchResultContainer({
  imageData,
  selectedMember,
  onBackClick,
  onAnotherSearchClick,
  mode
}: ImageSearchResultProps) {
  const navigate = useNavigate();
  const [isInitLoading, setIsInitLoading] = useState(true);
  const [searchResult, setSearchResult] = useState<ImageSearchResponseType | null>(null);

  const { mutateAsync: imageCompressMutation, isLoading: isImageCompressLoading } =
    useCompressImageMutation();
  const {
    mutateAsync: imageSearchMutation,
    isLoading: isImageSearchLoading,
    isError: isImageSearchError
  } = useSearchImageMutation();

  const isLoading = isInitLoading || isImageCompressLoading || isImageSearchLoading;

  /**
   * checkIsFailureResult : true 반환 시 검색 실패
   * 1. searchResult.success가 false이거나, 결과 수(numCards)가 0인 경우
   * 2. 결과는 존재하지만, 모든 결과의 첫 번째 후보 유사도가 MIN_SIMILARITY_THRESHOLD 미만인 경우
   */
  const checkIsFailureResult = () => {
    if (!searchResult?.success) return true;
    if (searchResult?.numCards === 0) return true;
    if (
      searchResult?.results.every((result) => {
        if (result.candidates.length === 0) return true;
        return result.candidates[0].similarity < MIN_SIMILARITY_THRESHOLD;
      })
    ) {
      return true;
    }
    return false;
  };

  //biome-ignore lint/correctness/useExhaustiveDependencies: "useEffect"
  useEffect(() => {
    const imageSearch = async () => {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      const compressedImage = await imageCompressMutation({ imageData });
      const result = await imageSearchMutation({ imageData: compressedImage, selectedMember });
      setSearchResult(result);
      setIsInitLoading(false);
    };

    imageSearch();
  }, []);

  //biome-ignore lint/correctness/useExhaustiveDependencies: checkIsFailureResult은 searchResult에 의존하므로 별도 추가 불필요
  useEffect(() => {
    if (!isLoading && searchResult && !checkIsFailureResult()) {
      if (mode === 'sell') {
        sessionStorage.setItem('sell-mode-selected-member', JSON.stringify(selectedMember));
        navigate('/new-my-trading-photo-cards/set-price');
      }
      if (mode === 'exchange') {
        sessionStorage.setItem('exchange-mode-selected-member', JSON.stringify(selectedMember));
        navigate('/new-my-trading-photo-cards/set-exchange-card');
      }
    }
  }, [isLoading, searchResult, mode, selectedMember, navigate]);

  if (isLoading) {
    return (
      <ImageSearchLoading
        imageData={imageData}
        selectedMember={selectedMember}
        isLoading={isLoading}
        onBackClick={onBackClick}
      />
    );
  }

  if (checkIsFailureResult() || searchResult === null || isImageSearchError) {
    return (
      <ImageSearchFailure
        imageData={imageData}
        selectedMember={selectedMember}
        onBackClick={onBackClick}
        onAnotherSearchClick={onAnotherSearchClick}
      />
    );
  }

  if (mode === 'sell' || mode === 'exchange') return null;

  return <ImageSearchSuccessContainer onBackClick={onBackClick} selectedMember={selectedMember} />;
}
